// AppShell — top-level layout that wraps every v2 dashboard route.
//
// Composition: Sidebar (left rail) + TopBar (sticky header) + MainContent (children).
// The CHG-1223 <CommandPalette /> is mounted by App.tsx (above this shell) so it
// can render its modal at document-root z-index without being constrained to the
// content column. The shell's TopBar ⌘K button dispatches a synthetic Meta+K
// keydown that the palette's global window listener catches — the contract
// is intentionally one-way (shell → palette) so the palette stays immutable.
//
// Per .forge/plans/v2-nav-shell.md.
//
// CHG-1422 / ITER-3542 — partial DRIFT-651 close. The sidebar's 248px expanded
// width was eating 66% of a 375px phone viewport on first load, leaving content
// usability near-zero until the user found the hamburger. AppShell now reads
// `(max-width: 768px)` on mount and starts the sidebar collapsed when the
// viewport matches. Explicit `initialSidebarCollapsed` still wins (tests,
// preview, storybook callers). We deliberately do NOT subscribe to viewport
// resize for the COLLAPSED STATE — auto-collapsing AFTER the user explicitly
// expanded would be too magical; once the user toggles, their choice persists
// for the session.
//
// CHG-1427 / ITER-3547 — DRIFT-657 close. Even after CHG-1422 the collapsed
// sidebar's 64px chrome claimed 17% of a 375px viewport. The shell now
// SUBSCRIBES to viewport changes (separate concern from initial state) via
// `useNarrowViewport()` and passes `narrow` down to Sidebar, which uses it to
// switch between sticky (wide) and overlay (narrow + expanded) modes — when
// narrow + collapsed, Sidebar renders at width:0 so content gets the full
// viewport. The `onCollapse` callback lets the Sidebar's backdrop dismiss the
// overlay; AppShell wires it to setCollapsed(true).

import { useCallback, useState, type ReactNode } from "react";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { useNarrowViewport } from "./useNarrowViewport";

export interface AppShellProps {
  children: ReactNode;
  // Optional override of the sidebar's initial collapsed state — production
  // callers omit this. Tests / Storybook can pre-collapse for snapshot.
  // When undefined, AppShell auto-detects via matchMedia (CHG-1422).
  initialSidebarCollapsed?: boolean;
}

// CHG-1422 — viewport-narrow breakpoint for the mobile auto-collapse. 768px
// matches the conventional sm/md boundary used by Tailwind + most design
// systems. SSR-safe: returns false when window or matchMedia is unavailable
// so the server render matches a desktop default.
const NARROW_VIEWPORT_QUERY = "(max-width: 768px)";

function detectNarrowViewport(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(NARROW_VIEWPORT_QUERY).matches;
}

export function AppShell({ children, initialSidebarCollapsed }: AppShellProps) {
  // CHG-1422 — when the caller didn't override, derive initial collapse from
  // viewport width. useState's lazy initializer runs once on mount so we
  // don't re-detect on every render or on viewport resize.
  const [collapsed, setCollapsed] = useState(() =>
    initialSidebarCollapsed ?? detectNarrowViewport(),
  );
  const toggleSidebar = useCallback(() => setCollapsed((prev) => !prev), []);
  // CHG-1427 — subscribed narrow detection drives Sidebar's overlay-vs-sticky
  // rendering mode. Reacts to viewport changes (rotation, resize) without
  // mutating the user's collapsed choice — distinct concern from the
  // mount-only `detectNarrowViewport()` above.
  const narrow = useNarrowViewport();
  const closeSidebar = useCallback(() => setCollapsed(true), []);

  return (
    <div
      data-testid="app-shell"
      style={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
        background: "var(--color-bg)",
        color: "var(--color-text)",
        fontFamily: "var(--font-sans, 'Inter', system-ui, sans-serif)",
      }}
    >
      <Sidebar collapsed={collapsed} narrow={narrow} onCollapse={closeSidebar} />
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <TopBar onToggleSidebar={toggleSidebar} />
        {/*
          Content wrapper. NOT a <main> element — page components (Donors,
          DonorDetail, PlaceholderIndex, etc.) own their own <main role="main">
          and content padding. AppShell only constrains max-width so visual
          chrome stays consistent across routes; the page's own padding is
          honored (no double-pad, no nested-main per CLAUDE.md RULE 14).
        */}
        <div
          data-shell-content
          style={{
            flex: 1,
            width: "100%",
            maxWidth: 1440,
            margin: "0 auto",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
